import { useTenantApi } from '@/account/hook/useTenantApi';
import { cartQueryKey, completeRegistrationUpsell, listOrdersQueryKey, loadPaymentOptions, paymentOptionQueryKey, processFreeOrder, registrationQueryKey, type Order, type paymentOptionType, type ProcessFreeOrderRequest, type Registration, type RegistrationUpsellCompleteRequest } from '@/api/registration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { StandardSpinner } from '@/base/QueryContainer/QueryContainer';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import { UiButton, UiHStack, UiIconCaretLeft, UiIconCaretRight, UiStack, UiText } from '@/lib/ui';
import { createFormatPrice } from '@/lib/util/locale';
import InfoBox from '@/registration/component/InfoBox';
import RegisterLayout from '@/registration/component/Register/Layout';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useRegisterUpsellSteps } from '@/registration/hook/useRegisterUpsellSteps';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState, type FC } from 'react';
import PayByCard from './PayByCard';
import PayByInvoice from './PayByInvoice';
import BaseThemeButton from '@/base/ThemeButton';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';

interface PaymentProps {
  isUpsell?: boolean
}

const PRICE_ZERO = 0;
const Payment: FC<PaymentProps> = ({ isUpsell }) => {
  const priceCurrency = 'AUD';
  const priceLocale = 'en-AU';
  const formatPrice = useMemo(
    () => { return createFormatPrice({ locale: priceLocale, currency: priceCurrency }); },
    []
  );
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const [errors, setErrors] = useState<string[]>([]);
  const cartQuery = useCartQuery(eventId);
  const { toNextStep: defaultToNextStep, toPreviousStep: defaultToPreviousStep } = useRegisterSteps();
  const { toNextStep: upsellToNextStep, toPreviousStep: upsellToPreviousStep } = useRegisterUpsellSteps();
  const { userSession } = useUserSession();
  const queryClient = useQueryClient();
  const registrationQuery = useRegistrationQuery(eventId);

  const toNextStep = isUpsell ? upsellToNextStep : defaultToNextStep;
  const toPreviousStep = isUpsell ? upsellToPreviousStep : defaultToPreviousStep;

  const { mutate: mutateUpsell } = useMutation<ApiResponseSingle<Registration>, Error, RegistrationUpsellCompleteRequest>(
    {
      mutationFn: async (data: RegistrationUpsellCompleteRequest) => {
        return await completeRegistrationUpsell(createTenantApiRequest)(data);
      },
      onSuccess: async (data: ApiResponseSingle<Registration>) => {
        if (data.item) {
          await queryClient.invalidateQueries({ queryKey: [registrationQuery] });
          await queryClient.invalidateQueries({ queryKey: [listOrdersQueryKey, { eventId, userSession }] });
          await queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
          await queryClient.invalidateQueries({ queryKey: [cartQueryKey, { eventId, userSession }] });
        } else {
          throw new Error('Failed to complete registration');
        }
      },
      onError: (error: any) => {
        throw error;
      }
    }
  );

  const { mutate: processFreeOrderMutate, isLoading: isProcessFreeOrderLoading } = useMutation<ApiResponseSingle<Order>, Error, ProcessFreeOrderRequest>({
    mutationFn: async (preparePaymentData: ProcessFreeOrderRequest) => {
      return await processFreeOrder(createTenantApiRequest)(preparePaymentData);
    },
    onSuccess: async (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        mutateUpsell({ registrationId: registrationQuery?.data?.id ?? 0 });
        toNextStep();
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to completed registrations']);
    }
  });

  const totalPriceZero = cartQuery.data?.totalAmount === PRICE_ZERO;

  const onSubmitFreeOrder = () => {
    if (!cartQuery.data) {
      return;
    }
    const carInfo: ProcessFreeOrderRequest = {
      cartId: cartQuery.data.id
    };
    processFreeOrderMutate(carInfo);
  };

  const paymentOptionQuery = useQuery(
    [paymentOptionQueryKey],
    async () => { return (await loadPaymentOptions(createTenantApiRequest)({ eventId })).items; },
    { enabled: !isApiPreparing }
  );

  const checkHasPaymentGateway = useMemo(() => {
    return (optionName: paymentOptionType) => {
      return paymentOptionQuery.data?.find(option => { return option.name === optionName && option.enabled; });
    };
  }, [paymentOptionQuery.data]);

  if (cartQuery.isFetching && totalPriceZero) {
    return (
      <StandardSpinner />
    );
  }

  // Only show the error if there is an actual error.
  if (cartQuery.error && totalPriceZero) {
    return (
      <BaseMessageBarError>
        Failed to load the shopping cart.
      </BaseMessageBarError>
    );
  }

  return (
    <RegisterLayout>
      {totalPriceZero ? (
        <UiHStack alignItems={'center'} justifyContent={'center'}>
          <UiText variant={'h6'}>
            Your cart adds up to {formatPrice(0)} so there is nothing to pay. Please click &quot;Complete&quot; to finish your registration.
          </UiText>
        </UiHStack>
      ) : (
        <BaseTransitionSlideLeft>
          <UiStack flexGrow={1} spacing={8}>
            <InfoBox
              title={'Choose payment method'}
            />
            {!totalPriceZero &&
              <UiStack spacing={4}>
                {registrationQuery.data?.attendeeCategory.creditCardEnabled && checkHasPaymentGateway('stripe') && <PayByCard creditCardGateway='stripe' />}
                {registrationQuery.data?.attendeeCategory.creditCardEnabled && checkHasPaymentGateway('eWay') && <PayByCard creditCardGateway='eWay' />}
                {registrationQuery.data?.attendeeCategory.creditCardEnabled && checkHasPaymentGateway('secure_pay') && <PayByCard creditCardGateway='secure_pay' />}
                {registrationQuery.data?.attendeeCategory.invoiceEnabled && checkHasPaymentGateway('invoice') && <PayByInvoice isUpsell={isUpsell} />}
                {registrationQuery.data?.attendeeCategory.creditCardEnabled && checkHasPaymentGateway('nab_transact') && <PayByCard creditCardGateway='nab_transact' />}
              </UiStack>
            }
          </UiStack>
        </BaseTransitionSlideLeft>
      )}
      {errors.length > 0 && (
        <UiStack spacing={4} flexGrow={1} py={4}>
          {errors.map((error) => {
            return (
              <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
            );
          })}
        </UiStack>
      )}
      <UiStack height={96} />
      {totalPriceZero ? (
        <LayoutFooter>
          <UiHStack justifyContent={'space-between'} flexGrow={1} py={{base: 1, lg: 2}}>
            <UiButton
              size={'lg'}
              colorScheme={'primary'}
              onClick={toPreviousStep}
              variant={'ghost'}
              fontSize={'lg'}
              leftIcon={(
                <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>
              )}
            >
              Prev
            </UiButton>
            <UiButton
              size={'lg'}
              colorScheme={'primary'}
              onClick={onSubmitFreeOrder}
              isLoading={isProcessFreeOrderLoading}
              variant={'ghost'}
              fontSize={'lg'}
              rightIcon={(
                <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>
              )}
            >
              Complete
            </UiButton>
          </UiHStack>
        </LayoutFooter>
      ) : (
        <LayoutFooter>
          <UiHStack justifyContent={'space-between'} flexGrow={1}>
            {/*<UiButton*/}
            {/*  size={'lg'}*/}
            {/*  colorScheme={'primary'}*/}
            {/*  onClick={toPreviousStep}*/}
            {/*  variant={'ghost'}*/}
            {/*  fontSize={'lg'}*/}
            {/*  leftIcon={(*/}
            {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
            {/*  )}*/}
            {/*>*/}
            {/*  Previous*/}
            {/*</UiButton>*/}

            <BaseThemeButton
              theme={'gray'}
              onClick={toPreviousStep}
              leftIcon={(
                <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
              )}
            >
              Prev
            </BaseThemeButton>
            <BrandFooter/>
            <UiStack width={24}/>

          </UiHStack>
        </LayoutFooter>
      )}
    </RegisterLayout>
  );
};

export default Payment;
